import React from 'react';
import styled from 'styled-components';
import { ethers } from 'ethers';
import { Card } from './StyledComponents';
import { useSystemStatus } from '../hooks/useSystemStatus';
import { CONTRACTS } from '../utils/contracts';

// ---------------------------------------------------------------------------
// System tab — every parameter and live figure, raw from the chain, with a
// one-line meaning per row. For readers who want to see how the machine is
// configured and what state it's in, not a pitch. Public; no wallet needed.
// ---------------------------------------------------------------------------

const Wrap = styled.div`
  grid-column: 1 / -1;
  display: flex; flex-direction: column; gap: 1.25rem;
`;

const Intro = styled.div`
  text-align: center; padding: 0.5rem 0 0;
  h2 { color: #ffd700; margin: 0 0 0.4rem; }
  p { color: #999; max-width: 700px; margin: 0 auto; font-size: 0.9rem; line-height: 1.5; }
`;

const GroupTitle = styled.h3`
  color: #ffd700; margin: 0 0 0.75rem; font-size: 1.05rem;
`;

const T = styled.div`
  display: grid;
  grid-template-columns: minmax(140px, 1fr) minmax(140px, auto) 2fr;
  gap: 0.15rem 1.25rem;
  font-size: 0.85rem;

  .k { color: #aaa; padding: 0.42rem 0; border-bottom: 1px solid #222; }
  .v { color: #fff; font-family: monospace; text-align: right; padding: 0.42rem 0; border-bottom: 1px solid #222; white-space: nowrap; }
  .m { color: #777; padding: 0.42rem 0; border-bottom: 1px solid #222; line-height: 1.45; }
  .hot { color: #ef4444; font-weight: 700; }
  .cold { color: #10b981; }

  @media (max-width: 700px) {
    grid-template-columns: 1fr auto;
    .m { display: none; }
  }
`;

const RAY = 10n ** 27n;
const fRad = (x, dp = 2) => Number(ethers.formatUnits(x, 45)).toLocaleString('en-US', { maximumFractionDigits: dp });
const fWad = (x, dp = 2) => Number(ethers.formatUnits(x, 18)).toLocaleString('en-US', { maximumFractionDigits: dp });
const fRayPct = (x, dp = 0) => (Number(ethers.formatUnits(x, 27)) * 100).toFixed(dp) + '%';
const fWadPct = (x, dp = 0) => (Number(ethers.formatUnits(x, 18)) * 100).toFixed(dp) + '%';
const trim = (n) => (Number.isInteger(n) ? n : n.toFixed(1));
const secs = (s) => {
  s = Number(s);
  if (s >= 86400) return trim(s / 86400) + ' days';
  if (s >= 3600) return trim(s / 3600) + ' h';
  if (s >= 60) return trim(s / 60) + ' min';
  return s + ' s';
};
const apy = (perSecondRay) => {
  const r = Number(ethers.formatUnits(perSecondRay, 27));
  return ((Math.pow(r, 31536000) - 1) * 100).toFixed(2) + '% APY';
};
const ago = (ts) => {
  const m = Math.floor((Date.now() / 1000 - ts) / 60);
  return m < 60 ? `${m} min ago` : `${(m / 60).toFixed(1)} h ago`;
};
const addrShort = (a) => a.slice(0, 8) + '…' + a.slice(-4);

const Row = ({ k, v, m, tone }) => (
  <>
    <div className="k">{k}</div>
    <div className={`v ${tone || ''}`}>{v}</div>
    <div className="m">{m}</div>
  </>
);

const SystemPage = () => {
  const s = useSystemStatus();
  if (s.isLoading) return <Wrap><Card style={{ textAlign: 'center', color: '#888' }}>Reading every parameter from the chain…</Card></Wrap>;
  if (s.error && !s.vat) return <Wrap><Card style={{ textAlign: 'center', color: '#ef4444' }}>Read failed: {s.error}</Card></Wrap>;

  const armed = s.dog.Hole > 0n;
  // Fee income: per-second rate (base+duty) compounded over a day, on each
  // ilk's drawn debt. `pending` is what has accrued since the last drip and
  // is not yet in the surplus figure above.
  const nowSec = Date.now() / 1000;
  const feeRates = Object.values(s.ilks).map((i) => ({
    debt: Number(ethers.formatUnits(i.Art * i.rate, 45)),
    perSec: Number(ethers.formatUnits(i.duty + s.jug.base, 27)),
    rho: Number(i.rho)
  }));
  const feePerDay = feeRates.reduce((a, f) => a + f.debt * (Math.pow(f.perSec, 86400) - 1), 0);
  const feePending = feeRates.reduce((a, f) => a + f.debt * (Math.pow(f.perSec, Math.max(0, nowSec - f.rho)) - 1), 0);
  const humpCold = s.vow.hump >= 10n ** 6n * 10n ** 45n;
  const parUsd = Number(ethers.formatUnits(s.oracle.par, 27));
  const oraclePlsUsd = Number(ethers.formatUnits(s.oracle.oraclePls, 27)) * parUsd;

  return (
    <Wrap>
      <Intro>
        <h2>System</h2>
        <p>
          Every parameter and live figure, read raw from the chain every 30 seconds.
          Contract addresses and the change history live on the Contracts tab; this is
          the current state of the machine.
        </p>
      </Intro>

      <Card>
        <GroupTitle>Ledger (Vat)</GroupTitle>
        <T>
          <Row k="system live" v={s.vat.live === 1n ? 'yes' : 'CAGED'} m="1 = operating; 0 would mean emergency shutdown" />
          <Row k="total debt" v={fRad(s.vat.debt) + ' JDAI'} m="all JDAI in existence, vault-backed plus unbacked" />
          <Row k="unbacked (vice)" v={fRad(s.vat.vice, 4) + ' JDAI'} m="JDAI whose backing debt was cancelled (test-era remnants live here)" />
          <Row k="global ceiling (Line)" v={fRad(s.vat.Line, 0) + ' JDAI'} m="hard cap on total debt across all collateral types" />
          <Row k="PLS locked (Classic)" v={fWad(s.locked.pls, 0) + ' PLS'} m="native PLS held by the Classic collateral adapter" />
          <Row k="WPLS locked (Vaults)" v={fWad(s.locked.wpls, 0) + ' WPLS'} m="wrapped PLS held by the vault collateral adapter" />
        </T>
      </Card>

      <Card>
        <GroupTitle>Collateral types (ilks)</GroupTitle>
        {Object.entries(s.ilks).map(([name, i]) => (
          <div key={name} style={{ marginBottom: '1rem' }}>
            <div style={{ color: '#ccc', fontWeight: 600, margin: '0.5rem 0' }}>{name}{name === 'PLS-A' ? ' — legacy, ceiling frozen' : ''}</div>
            <T>
              <Row k="debt drawn (Art×rate)" v={fWad((i.Art * i.rate) / RAY, 2) + ' JDAI'} m="what vaults of this type currently owe" />
              <Row k="ceiling (line)" v={fRad(i.line, 0) + ' JDAI'} m="max debt this collateral type may issue" />
              <Row k="min vault debt (dust)" v={fRad(i.dust, 0) + ' JDAI'} m="smallest allowed vault, so auctions stay meaningful" />
              <Row k="collateral ratio (mat)" v={fRayPct(i.mat)} m="minimum collateral value per unit of debt" />
              <Row k="stability fee (duty+base)" v={apy(i.duty + s.jug.base)} m="interest accruing to the system surplus" />
              <Row k="last fee accrual (rho)" v={ago(Number(i.rho))} m="the daily keeper drips this" />
              <Row k="risk-adjusted price (spot)" v={Number(ethers.formatUnits(i.spot, 27)).toPrecision(6) + ' JDAI/unit'} m="oracle price ÷ mat — what the ledger values collateral at" />
            </T>
          </div>
        ))}
      </Card>

      <Card>
        <GroupTitle>Oracle</GroupTitle>
        <T>
          <Row k="JDAI target (par)" v={'$' + parUsd.toFixed(4)} m="1/1000 oz of gold in USD — what 1 JDAI aims to be worth" />
          <Row k="oracle PLS price" v={'$' + oraclePlsUsd.toFixed(9)} m="derived from the ledger's spot × mat × par" />
          <Row k="last price update" v={ago(s.oracle.age)} m="the medianizer's timestamp; posted daily on ≥2% moves" />
          <Row k="signer quorum (bar)" v={s.oracle.bar.toString()} m="price posts require this many oracle signatures" />
        </T>
      </Card>

      <Card>
        <GroupTitle>System books (Vow)</GroupTitle>
        <T>
          <Row k="surplus" v={fRad(s.vow.dai, 4) + ' JDAI'} m="accumulated fees, penalties, and auction recoveries" />
          <Row k="fee income" v={'+' + feePerDay.toFixed(5) + ' /day'} m={'stability fees on the current debt book — ' + (feePerDay * 365).toFixed(3) + ' JDAI/yr at current debt and rates'} />
          <Row k="…pending (undripped)" v={feePending.toFixed(5) + ' JDAI'} m="earned since the last fee accrual; the daily keeper folds it into the surplus" />
          <Row k="bad debt (sin)" v={fRad(s.vow.sin, 4) + ' JDAI'} m="losses not yet cancelled against surplus" />
          <Row k="…queued (Sin)" v={fRad(s.vow.Sin, 4) + ' JDAI'} m="fresh losses inside the wait window" />
          <Row k="…on auction (Ash)" v={fRad(s.vow.Ash, 4) + ' JDAI'} m="losses currently being recapitalized" />
          <Row k="loss queue delay (wait)" v={secs(s.vow.wait)} m="losses age this long before debt auctions may cover them" />
          <Row k="surplus lot (bump)" v={fRad(s.vow.bump, 2) + ' JDAI'} m="size of each surplus auction" />
          <Row k="surplus buffer (hump)" v={humpCold ? 'unreachable (auctions OFF)' : fRad(s.vow.hump, 2) + ' JDAI'} m="retained cushion; only surplus above it is auctioned" tone={humpCold ? 'cold' : undefined} />
          <Row k="debt auction bid (sump)" v={fRad(s.vow.sump, 2) + ' JDAI'} m="each debt auction covers exactly this much loss" />
          <Row k="debt auction lot (dump)" v={fWad(s.vow.dump, 0) + ' TKR'} m="opening TKR offer, bid downward by rescuers" />
        </T>
      </Card>

      <Card>
        <GroupTitle>Liquidations (Dog + Clippers)</GroupTitle>
        <T>
          <Row k="status" v={armed ? 'ARMED' : 'installed, not active'} tone={armed ? 'hot' : 'cold'}
            m={armed ? 'unsafe vaults can be liquidated right now' : 'every ceiling is zero — liquidation is impossible until activated (announced in advance)'} />
          <Row k="global auction cap (Hole)" v={fRad(s.dog.Hole, 0) + ' JDAI'} m="max debt in liquidation at once, all types" />
          <Row k="in auction now (Dirt)" v={fRad(s.dog.Dirt, 2) + ' JDAI'} m="currently being auctioned" />
        </T>
        {Object.entries(s.clips).map(([name, c]) => (
          <div key={name} style={{ marginTop: '0.9rem' }}>
            <div style={{ color: '#ccc', fontWeight: 600, margin: '0.4rem 0' }}>{name} auctions</div>
            <T>
              <Row k="per-type cap (hole)" v={fRad(s.dog.ilks[name].hole, 0) + ' JDAI'} m="max of this collateral in liquidation at once" />
              <Row k="penalty (chop)" v={fWadPct(s.dog.ilks[name].chop - 10n ** 18n)} m="added to the debt when a vault is liquidated" />
              <Row k="start premium (buf)" v={fRayPct(c.buf)} m="auctions start this far above the oracle price" />
              <Row k="price curve" v={fRayPct(s.calc.cut, 0) + ' per ' + secs(s.calc.step)} m="Dutch decay — price falls until someone buys" />
              <Row k="reset after" v={secs(c.tail) + ' or ' + fRayPct(c.cusp) + ' of start'} m="an unsold auction must be restarted past either bound" />
              <Row k="keeper reward" v={fRad(c.tip, 2) + ' JDAI + ' + fWadPct(c.chip)} m="paid to whoever triggers or restarts an auction" />
              <Row k="lifetime auctions" v={c.kicks.toString() + ' (' + c.count.toString() + ' active)'} m="history counter · currently open" />
            </T>
          </div>
        ))}
      </Card>

      <Card>
        <GroupTitle>Auction houses (Flapper / Flopper)</GroupTitle>
        <T>
          <Row k="surplus house (flap)" v={(s.flap.live === 1n ? 'live' : 'caged') + ' · ' + s.flap.kicks + ' lifetime'} m="sells surplus JDAI for TKR, burns the TKR" />
          <Row k="debt house (flop)" v={(s.flop.live === 1n ? 'live' : 'caged') + ' · ' + s.flop.kicks + ' lifetime'} m="mints TKR for JDAI to cover uncovered losses" />
          <Row k="min bid step (beg)" v={fWadPct(s.flap.beg - 10n ** 18n)} m="each bid must improve on the last by this much" />
          <Row k="bid window (ttl)" v={secs(s.flap.ttl)} m="a standing bid wins after this long unchallenged" />
          <Row k="auction lifetime (tau)" v={secs(s.flap.tau)} m="max duration before an unbid auction must restart" />
          <Row k="flop lot reflation (pad)" v={fWadPct(s.flop.pad - 10n ** 18n)} m="each restart of an unbid debt auction offers this much more TKR" />
        </T>
      </Card>

      <Card>
        <GroupTitle>Tokens</GroupTitle>
        <T>
          <Row k="JDAI in wallets" v={fWad(s.tokens.jdaiSupply, 2)} m="ERC-20 supply — internal ledger balances live in the Vat" />
          <Row k="TKR supply" v={fWad(s.tokens.tkrSupply, 1)} m="the system's lifetime P&L: losses mint it, profits burn it" />
          <Row k="TKR mint/burn guard" v={addrShort(s.tokens.tkrAuthority)} m="MkrAuthority — burning open to all, minting debt-auction-only" />
          <Row k="TKR owner" v={addrShort(s.tokens.tkrOwner)} m="administrative owner (single key until the multisig migration)" />
        </T>
        <p style={{ color: '#666', fontSize: '0.78rem', marginTop: '0.9rem' }}>
          Verified sources and the full configuration history for every contract above:
          see the Contracts tab. Deployer/admin: {addrShort('0x40a030B7287A3aADb2C5a4595A58500AA6Fb4a62')}.
          Vow {addrShort(CONTRACTS.VOW)} · Dog {addrShort(CONTRACTS.DOG)} · Flapper {addrShort(CONTRACTS.FLAPPER)} · Flopper {addrShort(CONTRACTS.FLOPPER)}.
        </p>
      </Card>
    </Wrap>
  );
};

export default SystemPage;
